// Employee self-service ACA view. Reads the employer's engine result for this
// employee (GET /api/employees/aca/status) and the 1095-C documents shared
// with the employee. Nothing is computed here.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, FileText, Download, AlertTriangle, Calendar, Info } from "lucide-react";
import { format } from "date-fns";
import { Help, TermLabel, Explained, CodeHelp } from "@/pages/employer/aca/shared/Help";

interface ACAForm {
  id: number;
  year: number;
  formType: string;
  status: string;
  issueDate: string;
  downloadUrl?: string;
  originalFileName?: string;
}

interface MonthRow {
  month: number;
  employed: boolean;
  acaStatus: string;
  hours?: number | null;
  offered: boolean | null;
  offerPlanName: string | null;
  requiredContribution: number | null;
  affordability: string;
  safeHarbor: string | null;
  enrolled: boolean;
  lnapReason: string | null;
}

interface ACAStatus {
  taxYear: number;
  visible: boolean;
  computed?: boolean;
  computedAt?: string | null;
  employerIsAle?: boolean;
  formRequired?: "full_time" | "self_insured_enrolled" | null;
  summary?: { employedMonths: number; fullTimeMonths: number; lnapMonths: number; offeredMonths: number; enrolledMonths: number; affordableMonths: number; notAffordableMonths: number; pendingMonths: number; status: string };
  months?: MonthRow[];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const AFFORDABILITY_LABEL: Record<string, string> = {
  affordable: "Affordable", not_affordable: "Not Affordable", pending_data: "Pending Data", not_applicable: "Not Applicable", mixed: "Mixed",
};
const ACA_STATUS_LABEL: Record<string, string> = {
  full_time: "Full-time", non_full_time: "Not full-time", measuring: "Measurement period", not_employed: "Not employed", unknown: "Undetermined",
};
const SAFE_HARBOR_LABEL: Record<string, string> = { fpl: "Federal poverty line", rate_of_pay: "Rate of pay", w2: "W-2 wages" };
const LNAP_LABEL: Record<string, string> = {
  first_partial_month: "first partial month of employment", waiting_period: "waiting period", initial_measurement_period: "initial measurement period", first_ale_year: "employer's first year as an ALE",
};

export default function ACACompliance() {
  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState(currentYear - 1);
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const { data: acaStatus, isLoading: statusLoading, error: statusError } = useQuery<ACAStatus>({
    queryKey: ["/api/employees/aca/status", taxYear],
    queryFn: async () => {
      const response = await fetch(`/api/employees/aca/status?taxYear=${taxYear}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch ACA status");
      return response.json();
    },
  });

  const { data: acaForms = [], isLoading: formsLoading, error: formsError } = useQuery<ACAForm[]>({
    queryKey: ["/api/employees/aca/forms"],
    queryFn: async () => {
      const response = await fetch("/api/employees/aca/forms", { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch ACA forms");
      return response.json();
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "available": return <Badge variant="default" className="bg-green-100 text-green-800">Available</Badge>;
      case "pending": return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case "processing": return <Badge variant="outline">Processing</Badge>;
      default: return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const summary = acaStatus?.summary;
  const rollup = summary ? AFFORDABILITY_LABEL[summary.status] ?? summary.status : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-3xl font-bold">ACA Compliance</h1>
        <div className="flex items-center gap-2">
          <Select value={String(taxYear)} onValueChange={(v) => setTaxYear(Number(v))}>
            <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          {acaStatus?.computed && rollup && (
            <Badge variant="outline" className="text-sm"><Shield className="h-4 w-4 mr-1" />{rollup}</Badge>
          )}
        </div>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Your employer reports the health coverage it offered you to the IRS on Form 1095-C. The month-by-month view below is the same record your employer uses.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5" /> Your ACA status for {taxYear}</CardTitle>
        </CardHeader>
        <CardContent>
          {statusLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {statusError && (
            <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>Failed to load ACA information. Please try again or contact HR.</AlertDescription></Alert>
          )}
          {acaStatus && acaStatus.visible === false && (
            <p className="text-sm text-muted-foreground">ACA information is not available in the employee portal for your employer. Contact HR with questions.</p>
          )}
          {acaStatus && acaStatus.visible !== false && acaStatus.computed === false && (
            <p className="text-sm text-muted-foreground">Your employer has not run ACA reporting for {taxYear} yet.</p>
          )}
          {acaStatus?.computed && summary && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><p className="text-muted-foreground"><TermLabel term="fullTime">Full-time months</TermLabel></p><p className="text-lg font-medium">{summary.fullTimeMonths} / 12</p></div>
                <div><p className="text-muted-foreground"><TermLabel term="offer">Months offered coverage</TermLabel></p><p className="text-lg font-medium">{summary.offeredMonths}</p></div>
                <div><p className="text-muted-foreground"><TermLabel term="enrolledMonthForms">Months enrolled</TermLabel></p><p className="text-lg font-medium">{summary.enrolledMonths}</p></div>
                <div><p className="text-muted-foreground"><TermLabel term={summary.status === "pending_data" ? "pendingData" : summary.status === "not_applicable" ? "notApplicable" : "affordability"}>Affordability</TermLabel></p><p className="text-lg font-medium">{rollup}</p></div>
              </div>
              <div className="text-sm">
                <span className="font-medium"><TermLabel term="formRequired">Form 1095-C:</TermLabel> </span>
                {acaStatus.formRequired ? "Your employer will provide a Form 1095-C for this year." : "A Form 1095-C is not required for you for this year."}
                {acaStatus.employerIsAle === false && <span className="text-muted-foreground"> Your employer is not an <TermLabel term="ale">Applicable Large Employer</TermLabel> for this year.</span>}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-2">Month</th>
                      <th className="text-left p-2"><TermLabel term="fullTime">Status</TermLabel></th>
                      {acaStatus.months?.some((m) => m.hours != null) && <th className="text-left p-2"><TermLabel term="hoursOfService">Hours</TermLabel></th>}
                      <th className="text-left p-2"><TermLabel term="offer">Coverage offered</TermLabel></th>
                      <th className="text-left p-2"><TermLabel term="employeeRequiredContribution">Your monthly cost (self-only)</TermLabel></th>
                      <th className="text-left p-2"><TermLabel term="affordability">Affordability</TermLabel></th>
                      <th className="text-left p-2"><TermLabel term="enrolledMonthForms">Enrolled</TermLabel></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(acaStatus.months ?? []).map((m) => (
                      <tr key={m.month} className="border-t">
                        <td className="p-2">{MONTHS[m.month - 1]}</td>
                        <td className="p-2">
                          {m.employed ? (ACA_STATUS_LABEL[m.acaStatus] ?? m.acaStatus) : "Not employed"}
                          {m.lnapReason && <span className="block text-xs text-muted-foreground"><TermLabel term="lnap">Limited non-assessment period ({LNAP_LABEL[m.lnapReason] ?? m.lnapReason})</TermLabel></span>}
                        </td>
                        {acaStatus.months?.some((x) => x.hours != null) && <td className="p-2">{m.hours == null ? "—" : m.hours.toFixed(1)}</td>}
                        <td className="p-2">{m.offered ? (m.offerPlanName ?? "Yes") : m.employed ? "No" : "—"}</td>
                        <td className="p-2 font-mono">{m.requiredContribution == null ? "—" : `$${m.requiredContribution.toFixed(2)}`}</td>
                        <td className="p-2">
                          {AFFORDABILITY_LABEL[m.affordability] ?? m.affordability}
                          {m.safeHarbor && m.affordability === "affordable" && <span className="block text-xs text-muted-foreground"><TermLabel term={m.safeHarbor === "fpl" ? "fplSafeHarbor" : m.safeHarbor === "w2" ? "w2SafeHarbor" : m.safeHarbor === "rate_of_pay" ? "rateOfPaySafeHarbor" : "safeHarbor"}>{SAFE_HARBOR_LABEL[m.safeHarbor] ?? m.safeHarbor} safe harbor</TermLabel></span>}
                        </td>
                        <td className="p-2">{m.enrolled ? "Yes" : m.employed ? "No" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {acaStatus.computedAt && <p className="text-xs text-muted-foreground">Last calculated {new Date(acaStatus.computedAt).toLocaleString()}.</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> ACA Forms (1095-C)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Alert>
              <Calendar className="h-4 w-4" />
              <AlertDescription>Form 1095-C reports the health coverage offered to you. Keep it with your tax records; it does not need to be attached to your return.</AlertDescription>
            </Alert>
            {formsLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {formsError && <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>Failed to load forms.</AlertDescription></Alert>}
            <div className="space-y-3">
              {acaForms.length > 0 ? acaForms.map((form) => (
                <div key={form.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">Form {form.formType} - {form.year}</p>
                        {getStatusBadge(form.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">Issue Date: {format(new Date(form.issueDate), "MMMM d, yyyy")}</p>
                      {form.originalFileName && <p className="text-xs text-muted-foreground">File: {form.originalFileName}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {form.status === "available" && form.downloadUrl ? (
                      <Button variant="outline" size="sm" onClick={() => window.open(form.downloadUrl, "_blank")}>
                        <Download className="h-4 w-4 mr-2" /> Download PDF
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" disabled>Not Available</Button>
                    )}
                  </div>
                </div>
              )) : (
                !formsLoading && (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">No ACA forms available yet</p>
                    <p className="text-sm text-muted-foreground">Forms appear here once your employer generates them.</p>
                  </div>
                )
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Important Information</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">What is Form 1095-C?</h4>
              <p className="text-muted-foreground">It reports the health coverage your employer offered you each month, your share of the lowest-cost self-only option, and whether you were enrolled. Employers must furnish it by early March for the prior tax year.</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">What does "Limited non-assessment period" mean?</h4>
              <p className="text-muted-foreground">Months such as a waiting period or an initial measurement period during which the employer is not yet required to offer coverage.</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Questions?</h4>
              <p className="text-muted-foreground">Contact HR or Benefits Administration about your ACA status or forms.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
